// ════════════════════════════════════════════════════════════════════
// TERROR — M9 · THE LANGUAGE SCAFFOLD
// ────────────────────────────────────────────────────────────────────
// "Strings out of components into a lang table."  — the roadmap, M9
//
// ── THE API IS THE WHOLE DESIGN ──────────────────────────────────────
//
//     t("plaza.wars.title", "Faction Wars")
//     t("titles.progress", "{have} of {need} families closed", { have, need })
//
// The ENGLISH LIVES AT THE CALL SITE, as the second argument. That one
// decision is what makes this scaffold usable instead of aspirational,
// and it is worth being explicit about why, because the obvious design
// is the other one.
//
// The obvious design is t("plaza.wars.title") reading from a table. It
// has three failure modes and Terror would hit all three:
//
//   1. BIG BANG OR NOTHING. Every converted line must land in the table
//      in the same commit or the UI shows a raw key. With ~2,000 strings
//      across files up to 700KB, that is a rewrite nobody schedules, so
//      it never starts. Here, converting ONE line is a safe commit.
//   2. A MISSING KEY IS A VISIBLE BUG. `plaza.wars.title` on screen in
//      front of a player. Here, a missing key is invisible: the fallback
//      IS the English, so an unconverted or mistyped key renders exactly
//      what it rendered before.
//   3. THE TABLE ROTS. Somebody edits the English in the table, the call
//      site is elsewhere, and nobody notices the two have parted. Here
//      the English and the key are on the same line, always.
//
// The trade is that English is duplicated between the call sites and the
// generated table. That duplication is MECHANICAL, not manual —
// `node lang-extract.mjs` rebuilds STRINGS.en from the call sites and
// reports drift. A duplication a script owns is not a duplication.
//
// ── WHAT THIS IS AND IS NOT ──────────────────────────────────────────
// IS: the mechanism, the persistence, the hook, the extractor, the
// audit, and a worked pair of converted surfaces.
// IS NOT: a converted game. Terror has thousands of strings in files up
// to 700KB, and ripping them out is a mechanical pass with real
// regression risk that wants a dedicated session and a live QA lap. The
// scaffold exists so that pass can happen one FILE at a time, one
// commit at a time, with a working game after every single one.
//
// ── ⚠ THE ONE THING THIS DOES NOT SOLVE ──────────────────────────────
// Terror's voice does not translate. "Aenarchist", "Solrend", "Lyt",
// "Khao", "the Unstable World" — the canon vocabulary is invented and a
// translator either keeps it (and the sentence around it stops
// scanning) or replaces it (and it is no longer Terror). PROPER_NOUNS
// below is the beginning of the answer: the terms a translator must
// leave alone. Whether that list is the right shape is a caP'n
// question, and it is in the wrap-up.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";

export const DEFAULT_LANG = "en";

// Registered languages. A language with no table falls back to English
// per-key, so a half-translated language is a usable language — which is
// the only way a translation ever gets finished.
export const LANGS = {
  en: { id: "en", label: "English", native: "English", rtl: false },
};

// Canon vocabulary. A translator leaves every one of these alone; the
// grammar around them moves. This is a LIST, not a rule — it grows with
// the canon and nothing switches on its contents.
// Grouped so a translator can see WHY each one is untouchable, and so
// caP'n can veto a group rather than picking through a flat list.
// Expanded 2026-08-18 on his read — the four powers were already here,
// but the list was thin everywhere else.
export const PROPER_NOUNS = [
  // ── THE FOUR POWERS. Never translated, never abbreviated, never
  //    swapped for a genre word. "Anima" is not health, "Khao" is not
  //    mana, "Lyt" is not attack and "Lyf" is not defence — a
  //    translator reaching for the familiar word is the single most
  //    likely way this game stops being itself.
  "Anima", "Khao", "Lyt", "Lyf",

  // ── THE ARTS and what a being does with them
  "Primera", "Impera", "U'hlti", "Affinity", "Deviance",
  "Transcend", "Transcendence", "Rite", "Soul",

  // ── RANKS AND BEINGS. THE THREE-LEVEL LAW's own words.
  "Escling", "Escendant", "Beb", "Champion", "Lord",
  // ⚠ RENAMED 2026-08-26, and cards.js SUPREME_TITLES is still the
  // source of truth. caP'n: *"D'aeternuz = A'oeternis. A'eternuz =
  // A'aeternis."* Both old spellings — and the bare "Aeternuz" the
  // Rulebook and Tutorial used in eight places — are gone from the
  // project entirely. V'igiluz and Jester were NOT renamed.
  "Supreme Being", "A'aeternis", "A'oeternis", "V'igiluz", "Jester",
  "Aenarchist",
  // ⭐ THE FOUR AFFINITIES BY NAME (caP'n, 2026-09-13, settling two
  //    spellings in his own hand): *"Y'Tyranuz (capital 'T' and 'z' no
  //    's') Fiend or Faux (not Friend)"*. These are CARD NAMES and they
  //    are frozen as such — the never-say list bars a CHARACTER from
  //    speaking Y'Tyranuz or X'aol, it does not touch the printed label,
  //    and a translator normalising either spelling is deleting canon.
  //    X'aol is already frozen further down with the factions.
  "Y'Tyranuz", "X'aol", "Enlightenment", "Fiend or Faux",

  // ── THE TABLE
  "Solrend", "Shatterfield", "Grimoire", "Luxmoire", "Necropolis",
  "Rift", "Tome", "Curio", "Prophecy", "Nihil", "Omnia",
  "Ancient Sorcery", "Shadow Gambling", "Mulligan",
  // ⬡ THE TWO AXIS WORDS, AND WHICH IS WHICH — spelled out because the
  //   inversion has been made FOUR TIMES in this codebase and cost a
  //   silent achievement bug the fourth time.
  //     ILK    — the TOP-LEFT stamp.  Humanoid · Beast · Machine · Dragon
  //              (on a Tome/support it is that card's school of magic:
  //              Omnia, Ritual, Contract, Nihil, Construction, Treasure,
  //              Ancient Sorcery, Shadow Gambling — plus a Curio's
  //              "wears" / "ally" / "J'ynxi" tag).
  //     SCHOOL — the TOP-RIGHT stamp. Space · Phantom · Dragon · Time
  //              (on a support it is the broad Aenergy-Art term, which
  //              is the card's own type: Tome · U'hlti · Impera ·
  //              Affinity).
  //   caP'n, 2026-09-14, asked outright whether the top-right stamp is
  //   School or Class: *"school :3"*. It is School, everywhere, forever.
  //   ⚠ DRAGON IS A WORD ON BOTH AXES, which is why a swapped field can
  //   hide: three of twelve starter decks read correctly either way.
  //   A translator collapsing these two into one word deletes the
  //   Natural/Deviant distinction the whole growth system is built on.
  "Ilk", "School",
  // ⬡ THE TWO COMBAT STATES. Not the same thing and a being can be
  //   both: Fractured is a Lyf state (Lyf 0, still blocks); Fallen is a
  //   turn state (does not block, cannot be Lyt-attacked).
  "Fractured", "Fallen",
  // ⬡ THE REFUSED DEFEAT, and the tag on every persistent card.
  //   ATV = Active Till Void, and the letters are the definition.
  "Graveshift", "ATV",
  // ⬡ THE EXILE WAVE (caP'n, 2026-09-06/07). An Art is not a card, and
  //   "Exile" is a rift as well as a verb.
  "Exile", "Exiliation", "the Ritual", "Art", "Arts",

  // ── THE WORLD
  // ⬡ EMPERIUM vs MINERIUM (caP'n, 2026-08-18) — NOT a size ladder.
  //   An EMPERIUM is a universe/dimension: infinite, and a Lord's.
  //   A MINERIUM is FINITE — a traversable overworld with edges, built
  //   by a being under Lord rank, gifted by a Lord, or "other ways."
  //   Both are proper nouns and neither is ever translated.
  // "the Rise" deliberately absent: it is the LANDSCAPE doctrine's element
  // 6 (motes going up), not a Terror term, and Terror no longer refers
  // to it. It belongs to whichever screen draws a horizon.
  // ⬡ AND THE PLAZA IS AN EMPERIUM (caP'n, 2026-08-19), because V'ael's
  //   DIVINE JEWEL stands in it and houses her Avatar's Sol. Every
  //   overworld, forever, is a Minerium unless caP'n says otherwise.
  "the Unstable World", "the Illegal Empire", "Aenergy",
  "Emperium", "Minerium", "Avatar", "the Silver Lining", "Sol Shard",
  "the Seal", "the Blighted Marsh", "the Crest Well", "W'ily's Pier",
  "the Plaza", "Au Gratin", "Necropolis", "Sol", "Solrend",
  "Divine Jewel", "Sol King",

  // ⭐ THE NINE ZONES OF MINERIUM 1 (caP'n's re-trace, 2026-08-21).
  //   Every one of these is a PLACE NAME and a translator normalising
  //   one is a translator renaming a room the player has to find again.
  //   "the Silver Lining" and "the Blighted Marsh" above are kept even
  //   though the re-trace deleted both regions — a frozen string that
  //   names nothing costs nothing, and the day either name comes back
  //   it is already protected.
  "Boneyard South", "Harsh Marsh West", "SHV's Mausoleum",
  "Harsh Marsh East", "Deserted Desert", "Boneyard North",
  "SHV's Crystal Mausoleum", "the Eyeland", "the Crimson Dais",
  "the Roadhead", "the Crimson Jewel", "the Pupil",

  // ── MONEY. The symbols travel with the names: ◆ ◕ ◈
  "Divine Crest", "Divine Crests", "Chocolate Coin", "Chocolate Coins",
  "Snake Eyes Coin",

  // ── FACTIONS AND NAMES. Every apostrophe is load-bearing — the
  //    naming pattern IS the canon (X'aol = Exile, V'ael = Veil,
  //    Z'hefir = Zephyr), so a translator "fixing" the punctuation
  //    destroys the joke the whole cast is built on.
  "Terror", "J'ynxi", "Nullus", "Omnis", "Swords of Nullus",
  "Blades of Omnis", "J'ynxi's Jesters", "X'aol", "V'ael",
  "Z'hefir", "Sov'rn", "Rük", "S'uhnker", "T'yrent",
  // ⭐ EVERY SPELLING OF WILLIE IS A DIFFERENT MAN WEARING THE SAME
  //    NAME, and the spelling is the ONLY place the player is ever
  //    told. A translator normalising these to one form deletes the
  //    game's longest-running joke, so all of them are frozen.
  //    ⚠ "Willy" IS THE ONE THE PLAZA PUTS ON SCREEN — the groundskeeper
  //    at the front desk — and it was the one this list left out, which
  //    made it the single most likely spelling for a translator to
  //    normalise into "Willie" and collapse the very distinction the
  //    list exists to protect. "All of them" now means all of them.
  "Willie", "Willy", "Willee", "W'ily", "W'yll", "W'illiam", "W'aelly", "W'aolly", "Willey",
  "Sun", "Moon",
  // ⭐ THE LORDE LAW (caP'n, 2026-08-19). `Lord` is the rank, ungendered.
  //    `Lorde` is that same rank spoken of a woman — V'ael is a Lorde and
  //    may also be called a Lord; NO MAN IS EVER A LORDE. Both spellings
  //    are proper nouns and neither is ever "corrected" into the other.
  "Lord", "Lorde",
  // ⭐ THE FOUR RIFTS AND THEIR ARTS (R115, caP'n 2026-09-07). The rift
  //    carries the Art, not the being: "Body far left, then [Sol] to its
  //    Ryt, then Heart rift, and finally on the far right is the Mind
  //    Rift", pairing "(Body tank Lyf, mind warden chao, Heart Healer
  //    Lyt, Sol Dps Anima)". Sol and Terror are already frozen above as
  //    world terms; the other six were not frozen anywhere.
  "Body", "Heart", "Mind", "Tank", "Healer", "Warden",
  // The pair the player IS (2026-08-18) — parents following their
  // children's Aenergy. Two names, one player.
  "Lyraei", "Lestre",
  // Omnis is a faction AND a power that grants kingdoms.
  "The Great Omnis", "Sir H'osh Vrown", "Colonel",
  "Venus", "V'yen", "Willie's Shade",
];

// ── THE TABLES ────────────────────────────────────────────────────────
// STRINGS.en is GENERATED — run `node lang-extract.mjs` from the app
// root to rebuild it from the call sites. Editing it by hand is not
// wrong, it is just pointless: the next extraction overwrites it, and
// the call site is the source of truth by construction.
//
// A translated table is hand-written and only needs the keys it has.
export const STRINGS = {
  en: {
    // ── BEGIN GENERATED (lang-extract.mjs) ──
    "titles.header": "Titles",
    "titles.progress": "{have} of {need} families closed",
    "titles.blurb": "Every family of deeds ends in a sentence. Close the family, wear the sentence — it follows you into every game in the Empire.",
    "titles.wear": "WEAR",
    "titles.worn": "WORN",
    "wars.header": "FACTION WARS",
    "wars.empty": "Nothing won yet this week. The board opens with the first Solrend.",
    "wars.deadHeat": "Dead heat — {who} are level at {n}.",
    "wars.lead": "{faction} leads by {margin}.",
    "wars.spoil": "Your faction took {week}. You fought in it — the colours are yours until the next one settles.",
    "wars.missedIt": "{faction} took {week} without you. Win one this week and the next set of colours is yours.",
    "wars.left.days": "{d}d {h}h left",
    "wars.left.hours": "{h}h {m}m left",
    "wars.left.minutes": "{m}m left",
    "wars.settling": "settling",
    // ── END GENERATED ──
  },
};

// ── THE CURRENT LANGUAGE ──────────────────────────────────────────────
// Module-level, because t() is called from render paths that have no
// business threading a context through six components for a value that
// changes roughly never. Subscribers re-render on change.
let CURRENT = DEFAULT_LANG;
const listeners = new Set();

export const getLang = () => CURRENT;

export function setLang(id) {
  const next = LANGS[id] ? id : DEFAULT_LANG;
  if (next === CURRENT) return;
  CURRENT = next;
  for (const l of listeners) { try { l(next); } catch (_) {} }
}

// ── THE LOOKUP ────────────────────────────────────────────────────────
// t(key, english, vars)
//
// `english` is REQUIRED in practice and optional in signature: a call
// with no fallback returns the key, which is loud in playtest and
// harmless in production. Never throws, never returns undefined.
export function t(key, english = "", vars = null) {
  const table = STRINGS[CURRENT] || null;
  let out = (table && table[key]) || (STRINGS[DEFAULT_LANG] && STRINGS[DEFAULT_LANG][key]) || english || key;
  if (vars) out = interpolate(out, vars);
  return out;
}

// {name} substitution. A placeholder with no matching var is LEFT AS IS
// rather than replaced with "undefined" — a visible {name} in playtest
// names its own bug, and "undefined" names nothing.
export function interpolate(str, vars) {
  return String(str).replace(/\{(\w+)\}/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m
  );
}

// ── THE HOOK ──────────────────────────────────────────────────────────
// Any component that renders translated text calls this so it re-renders
// when the language changes. It returns t bound to nothing in
// particular — the binding is module-level — but taking it from the hook
// is what creates the subscription, and that is the whole point.
export function useLang() {
  const [lang, setLangState] = useState(CURRENT);
  useEffect(() => {
    const l = (next) => setLangState(next);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return { lang, t, setLang, langs: Object.values(LANGS) };
}

// ── PERSISTENCE ───────────────────────────────────────────────────────
// The choice rides the profile, so it follows the player across devices
// and across every IE title that adopts this file. Read once at boot.
export async function loadLangPreference(uid) {
  if (!uid) return DEFAULT_LANG;
  try {
    const snap = await get(ref(db, `profiles/${uid}/lang`));
    const id = snap.val();
    if (id && LANGS[id]) { setLang(id); return id; }
  } catch (_) { /* an unreadable preference is English, not an error */ }
  return DEFAULT_LANG;
}

export async function saveLangPreference(uid, id) {
  setLang(id);
  if (!uid) return false;
  try { await set(ref(db, `profiles/${uid}/lang`), LANGS[id] ? id : DEFAULT_LANG); return true; }
  catch (e) { console.warn("[lang] save failed (non-fatal):", e?.message); return false; }
}

// Mount ONCE at the top of App. Loads the player's saved language the
// moment they are known, and re-loads on account change. Costs one
// shallow read per sign-in and nothing at all after that.
export function useLangBoot() {
  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => {
      if (u?.uid) loadLangPreference(u.uid);
      else setLang(DEFAULT_LANG);
    });
  }, []);
}

// ── THE AUDIT ─────────────────────────────────────────────────────────
// What a translator still owes, per language. A function, not a chore.
export function auditLang(id) {
  const base = Object.keys(STRINGS[DEFAULT_LANG] || {});
  const target = STRINGS[id] || {};
  const missing = base.filter((k) => !(k in target));
  const orphan = Object.keys(target).filter((k) => !base.includes(k));
  return {
    lang: id, total: base.length,
    translated: base.length - missing.length,
    pct: base.length ? Math.round(((base.length - missing.length) / base.length) * 100) : 100,
    missing, orphan,
  };
}

// Every placeholder a string expects — so a translated line that drops
// {have} is caught by a script instead of by a player seeing a sentence
// with a hole in it.
export const placeholdersIn = (str) =>
  [...String(str).matchAll(/\{(\w+)\}/g)].map((m) => m[1]).sort();

export function auditPlaceholders(id) {
  const base = STRINGS[DEFAULT_LANG] || {};
  const target = STRINGS[id] || {};
  const bad = [];
  for (const [k, v] of Object.entries(target)) {
    if (!(k in base)) continue;
    const a = placeholdersIn(base[k]).join(","), b = placeholdersIn(v).join(",");
    if (a !== b) bad.push({ key: k, expected: a, got: b });
  }
  return bad;
}

const lang = {
  DEFAULT_LANG, LANGS, PROPER_NOUNS, STRINGS,
  getLang, setLang, t, interpolate, useLang,
  loadLangPreference, saveLangPreference, useLangBoot,
  auditLang, placeholdersIn, auditPlaceholders,
};
export default lang;
